import type { NationalIdRules } from '../types';

const strip = (value: string): string => value.replace(/[.\s-]/g, '').toUpperCase();

const validateChileanRut = (rut: string): boolean => {
  if (!rut || typeof rut !== 'string') {
    return false;
  }
  const cleanRut = strip(rut);
  if (cleanRut.length < 8 || cleanRut.length > 9) {
    return false;
  }
  const dv = cleanRut.slice(-1);
  const body = cleanRut.slice(0, -1);
  if (!/^\d+$/.test(body) || !/^[\dK]$/.test(dv)) {
    return false;
  }
  // Reject trivial repeating bodies (e.g. 11111111-1) that pass mod-11 by coincidence.
  if (/^(\d)\1+$/.test(body)) {
    return false;
  }
  const weights = [2, 3, 4, 5, 6, 7];
  let sum = 0;
  for (let i = body.length - 1, w = 0; i >= 0; i -= 1, w += 1) {
    sum += parseInt(body[i], 10) * weights[w % 6];
  }
  const remainder = sum % 11;
  const expected = remainder === 0 ? '0' : remainder === 1 ? 'K' : String(11 - remainder);
  return dv === expected;
};

/**
 * Canonical Chile RUT: digits + hyphen + DV (no thousand separators).
 * Example: 12345678-5
 */
const normalizeChileanRut = (value: string): string => {
  if (!value) {
    return '';
  }
  const cleaned = strip(value);
  if (cleaned.length < 2) {
    return cleaned;
  }
  return `${cleaned.slice(0, -1)}-${cleaned.slice(-1)}`;
};

/**
 * Display Chile RUT: xx.xxx.xxx-x
 * Heuristic matches legacy RutFormatUtils for incomplete values without a verifier.
 */
const formatChileanRut = (value: string): string => {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();
  const hasHyphenBeforeLast = /-[\dkK]$/i.test(trimmed);
  const cleaned = strip(trimmed);
  if (!cleaned) {
    return '';
  }

  let digits: string;
  let verifier: string | undefined;

  if (hasHyphenBeforeLast && cleaned.length > 1) {
    digits = cleaned.slice(0, -1);
    verifier = cleaned.slice(-1).toUpperCase();
  } else {
    const match = cleaned.match(/^(\d{1,8})([\dkK])?$/i);
    if (!match) {
      return value;
    }
    const allDigits = match[1];
    const lastChar = match[2];
    if (lastChar && /[kK]/i.test(lastChar)) {
      digits = allDigits;
      verifier = lastChar.toUpperCase();
    } else if (cleaned.length === 9) {
      digits = allDigits;
      verifier = lastChar;
    } else {
      digits = cleaned;
      verifier = undefined;
    }
  }

  if (!/^\d+$/.test(digits)) {
    return value;
  }

  const length = digits.length;
  let formatted = '';
  if (length <= 5) {
    formatted = digits;
  } else if (length === 6) {
    formatted = `${digits.slice(0, 3)}.${digits.slice(3)}`;
  } else if (length === 7) {
    formatted = `${digits.slice(0, 1)}.${digits.slice(1, 4)}.${digits.slice(4)}`;
  } else {
    formatted = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }

  return verifier ? `${formatted}-${verifier}` : formatted;
};

export const chileNationalId: NationalIdRules = {
  countryCode: 'CL',
  documentKind: 'RUT',
  labelKey: 'country.chile.idCard',
  invalidKey: 'country.chile.identifier.invalid',
  placeholder: '12.345.678-5',
  normalize: normalizeChileanRut,
  format: formatChileanRut,
  validate: validateChileanRut,
  searchToken: (value: string) => strip(value),
};
